import { Panel, PageHeader } from "@/components/admin-shell";
import { apiRequest, apiPaginatedRequest, IPaginatedResponse } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Search, Trash2, ChevronRight, ChevronLeft } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

interface IOrder {
  _id: string;
  orderNumber: string;
  shipping: {
    name: string;
    phone: string;
    district: string;
    address: string;
  };
  items: {
    name: string;
    qty: number;
  }[];
  paymentMethod: string;
  total: number;
  status: "pending" | "confirmed" | "packed" | "shipped" | "delivered" | "cancelled";
  createdAt: string;
}

const STATUSES = ["pending", "confirmed", "packed", "shipped", "delivered", "cancelled"];

export default function OrdersPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;

  // Construct query string for server-side QueryBuilder
  const queryParams = new URLSearchParams();
  queryParams.set("page", String(page));
  queryParams.set("limit", String(limit));
  if (filterStatus !== "all") {
    queryParams.set("status", filterStatus);
  }
  if (searchQuery.trim()) {
    queryParams.set("searchTerm", searchQuery.trim());
  }

  const queryString = queryParams.toString();

  // Fetch Orders with Server-side Searching, Filtering & Pagination
  const {
    data: response = { data: [], meta: { page: 1, limit: 10, total: 0, totalPages: 1 } },
    isLoading,
    error,
  } = useQuery<IPaginatedResponse<IOrder>>({
    queryKey: ["orders", filterStatus, searchQuery, page, limit],
    queryFn: () => apiPaginatedRequest<IOrder>(`/orders?${queryString}`),
  });

  const orders = response.data || [];
  const meta = response.meta || { page: 1, limit, total: orders.length, totalPages: 1 };

  // Update Status Mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      apiRequest<IOrder>(`/orders/${id}/status`, "PATCH", { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      toast.success("Order status updated successfully");
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to update order status");
    },
  });

  // Delete Order Mutation
  const deleteOrderMutation = useMutation({
    mutationFn: (id: string) => apiRequest(`/orders/${id}`, "DELETE"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      toast.success("Order deleted successfully");
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to delete order");
    },
  });

  const handleDelete = (id: string, orderNumber: string) => {
    if (
      window.confirm(
        `Are you sure you want to permanently delete order ${orderNumber}? This action cannot be undone.`,
      )
    ) {
      deleteOrderMutation.mutate(id);
    }
  };

  const fmt = (n: number) => `৳${n.toLocaleString("en-BD")}`;

  const handleStatusChange = (status: string) => {
    setFilterStatus(status);
    setPage(1);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setPage(1);
  };

  return (
    <>
      <PageHeader
        title="Orders & Fulfillment"
        subtitle="Track every order from payment receipt to doorstep delivery."
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <button
          onClick={() => handleStatusChange("all")}
          className={`rounded-full border px-4 py-1.5 text-xs uppercase tracking-widest transition cursor-pointer ${
            filterStatus === "all"
              ? "border-gold bg-gold text-primary-foreground font-semibold"
              : "border-gold/25 text-foreground/80 hover:border-gold/60"
          }`}
        >
          All
        </button>
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => handleStatusChange(s)}
            className={`rounded-full border px-4 py-1.5 text-xs uppercase tracking-widest transition cursor-pointer ${
              filterStatus === s
                ? "border-gold bg-gold text-primary-foreground font-semibold"
                : "border-gold/25 text-foreground/80 hover:border-gold/60"
            }`}
          >
            {s}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2 rounded-md border border-gold/20 bg-card/50 px-3 py-1.5 text-sm text-muted-foreground">
          <Search className="h-3.5 w-3.5" />
          <input
            placeholder="Search by order ID, name, phone…"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-56 bg-transparent outline-none text-foreground placeholder:text-muted-foreground/50"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center text-gold">Loading orders...</div>
      ) : error ? (
        <div className="text-red-400">Error loading orders: {(error as Error).message}</div>
      ) : (
        <Panel className="p-0 overflow-hidden flex flex-col">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-gold/15 bg-accent/30">
                <tr className="text-left text-[10px] uppercase tracking-[0.25em] text-gold-soft">
                  <th className="px-4 py-3 pl-5">Order Number</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">District</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Items</th>
                  <th className="px-4 py-3">Payment</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 pr-5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gold/10">
                {orders.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-5 py-12 text-center text-muted-foreground italic">
                      No orders found matching the filter or search query.
                    </td>
                  </tr>
                ) : (
                  orders.map((o) => {
                    const totalItems = o.items.reduce((sum, item) => sum + item.qty, 0);
                    return (
                      <tr
                        key={o._id}
                        onClick={() => navigate(`/orders/${o._id}`)}
                        className="hover:bg-accent/40 cursor-pointer group transition-colors"
                      >
                        <td className="px-4 py-3.5 first:pl-5 font-mono text-gold group-hover:underline">
                          {o.orderNumber}
                        </td>
                        <td className="px-4 py-3.5">
                          <div>
                            <div className="font-semibold">{o.shipping?.name || "N/A"}</div>
                            <div className="text-xs text-muted-foreground">
                              {o.shipping?.phone || ""}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3.5 text-muted-foreground">
                          {o.shipping?.district || "N/A"}
                        </td>
                        <td className="px-4 py-3.5 text-muted-foreground">
                          {new Date(o.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3.5">{totalItems}</td>
                        <td className="px-4 py-3.5">
                          <span className="rounded border border-gold/20 px-2 py-0.5 text-[11px] text-gold-soft uppercase">
                            {o.paymentMethod}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-right font-medium">{fmt(o.total)}</td>
                        <td className="px-4 py-3.5 last:pr-5" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-2">
                            <select
                              value={o.status}
                              onChange={(e) => {
                                const newStatus = e.target.value;
                                if (
                                  window.confirm(
                                    `Change order ${o.orderNumber} status to "${newStatus}"?`,
                                  )
                                ) {
                                  updateStatusMutation.mutate({ id: o._id, status: newStatus });
                                }
                              }}
                              className={`rounded border px-2 py-1 text-xs capitalize bg-accent/30 text-foreground outline-none focus:border-gold cursor-pointer ${
                                o.status === "delivered"
                                  ? "text-emerald-300 border-emerald-500/30"
                                  : o.status === "shipped"
                                    ? "text-blue-300 border-blue-500/30"
                                    : o.status === "pending"
                                      ? "text-gold border-gold/30"
                                      : "text-amber-300 border-amber-500/30"
                              }`}
                            >
                              {STATUSES.map((st) => (
                                <option key={st} value={st} className="bg-brand-card text-foreground">
                                  {st}
                                </option>
                              ))}
                            </select>
                            {o.status === "cancelled" && (
                              <button
                                onClick={() => handleDelete(o._id, o.orderNumber)}
                                className="rounded border border-red-500/30 bg-red-950/20 p-1 text-xs text-red-300 hover:bg-red-900/40 transition cursor-pointer flex items-center justify-center"
                                title="Delete Order"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3.5 pr-5">
                          <ChevronRight className="h-4 w-4 text-gold-soft opacity-50 group-hover:opacity-100 transition" />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Server-side Pagination Controls */}
          {meta.total > 0 && (
            <div className="flex items-center justify-between border-t border-gold/15 bg-accent/20 px-5 py-3 text-xs text-muted-foreground">
              <div>
                Showing{" "}
                <span className="font-semibold text-foreground">
                  {(meta.page - 1) * meta.limit + 1}
                </span>{" "}
                to{" "}
                <span className="font-semibold text-foreground">
                  {Math.min(meta.page * meta.limit, meta.total)}
                </span>{" "}
                of <span className="font-semibold text-gold">{meta.total}</span> orders
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={meta.page <= 1}
                  className="flex items-center gap-1 rounded border border-gold/20 bg-card px-3 py-1 text-foreground hover:bg-gold/10 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  <ChevronLeft className="h-3.5 w-3.5" /> Prev
                </button>
                <span className="px-2 font-medium text-foreground">
                  Page {meta.page} of {meta.totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
                  disabled={meta.page >= meta.totalPages}
                  className="flex items-center gap-1 rounded border border-gold/20 bg-card px-3 py-1 text-foreground hover:bg-gold/10 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  Next <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </Panel>
      )}
    </>
  );
}
